import React, { useEffect, useRef, useState } from 'react';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

const ConversationMessage = ({ message, onCommand }) => (
  message.role === 'user'
    ? <div className="ml-10 flex justify-end"><div className="max-w-[92%] rounded-lg bg-gray-100 px-3 py-2 text-sm leading-5 text-gray-800">{message.text}</div></div>
    : (
      <article className="border-l-2 border-accent pl-3">
        <div className="flex items-center gap-2"><img src="/kip-logo.png" alt="KIP" className="h-4 w-auto object-contain" /><span className="text-[11px] text-gray-400">Now</span></div>
        <p className="mt-1.5 whitespace-pre-line text-sm leading-6 text-gray-700">{message.text}</p>
        {(message.locationRows?.length > 0 || message.moves?.length > 0) && <div className="mt-2 space-y-1 text-[11px] text-gray-500">
          {message.locationRows?.map((location) => <div key={location.location_code}>{location.location_code} · {Number(location.used || location.inventory?.reduce((sum, row) => sum + Number(row.quantity || 0), 0) || 0)} · {location.pickable ? 'pickable' : 'reserve'}</div>)}
          {message.extraLocations > 0 && <div>+{message.extraLocations} more</div>}
          {message.moves?.slice(0, 4).map((move, index) => <div key={`${move.from}-${move.to}-${move.sku}-${index}`}>{move.quantity ?? '?'} × {move.sku}: {move.from} → {move.to}</div>)}
          {message.moves?.length > 4 && <div>+{message.moves.length - 4} more</div>}
        </div>}
        {message.governanceRefs?.length > 0 && <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500">
          {message.governanceRefs.map((ref) => ref.url
            ? <a key={`${ref.kind}-${ref.id}`} href={ref.url} target="_blank" rel="noreferrer" className="underline hover:text-gray-900">{ref.title}</a>
            : <span key={`${ref.kind}-${ref.id}`}>{ref.title}</span>)}
        </div>}
        {message.suggestedCommands?.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5">
          {message.suggestedCommands.map((command) => <button key={command} type="button" onClick={() => onCommand(command)} className="rounded-full border border-gray-300 px-2 py-1 text-[11px] text-gray-600 hover:border-gray-900 hover:text-gray-900">{command}</button>)}
        </div>}
      </article>
    )
);

export const askPayload = ({ focus, question, messages }) => ({
  question,
  focus,
  history: messages
    .filter((message) => message.role === 'user' || message.role === 'kip')
    .slice(-20)
    .map((message) => ({ role: message.role, text: message.text })),
});

const kipErrorMessage = (requestError) => {
  const status = requestError.response?.status;
  const data = requestError.response?.data || {};
  const detail = typeof data.detail === 'string' ? data.detail : '';
  if (status === 409) return 'KIP reasoning is not configured for this environment yet. Deterministic commands still work here.';
  if (status === 403) return 'Your Eadin account is not linked to a KIP user yet; ask an admin to add you in KIP.';
  if (status === 424) {
    if (data.reason === 'auth_rejected') return 'KIP rejected Eadin\'s service credential. An admin needs to re-mint the eadin credential in KIP.';
    if (data.reason === 'route_not_found') return 'Eadin is pointed at a KIP URL that has no warehouse endpoint (check KIP_API_BASE_URL).';
    if (data.reason === 'payload_rejected') return `KIP rejected the warehouse context${detail ? ` (${detail})` : ''}.`;
    if (data.reason === 'upstream_failed') return `KIP could not finish reasoning${detail ? ` (${detail})` : ''}. Try again in a moment.`;
    if (data.reason === 'timeout') return 'KIP took too long to answer. Try again in a moment.';
    if (data.reason === 'network_error') return 'Eadin could not reach KIP (network error).';
    if (data.reason === 'malformed_response') return 'KIP returned an unreadable answer.';
    return 'KIP did not answer.';
  }
  if (!requestError.response && requestError.request) return 'Eadin\'s server could not be reached.';
  return `Eadin's server did not answer (HTTP ${status}).`;
};

const ConversationPane = ({ model, focus, onSceneCommand, onRefresh }) => {
  const [draft, setDraft] = useState('');
  const [messages, setMessages] = useState([{
    role: 'kip',
    text: 'Warehouse twin is connected. I can find locations, SKUs, racks, capacity, blocked locations, ShipHero drift, and answer reasoning questions with KIP.',
  }]);
  const scrollRef = useRef(null);
  const composerRef = useRef(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);
  useEffect(() => {
    const element = composerRef.current;
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, 200)}px`;
  }, [draft]);

  const submit = async (eventOrText) => {
    if (typeof eventOrText !== 'string') eventOrText.preventDefault();
    const text = (typeof eventOrText === 'string' ? eventOrText : draft).trim();
    if (!text) return;
    const pendingMessage = { role: 'kip', text: 'Asking KIP…', pending: true };
    setMessages((current) => [...current, { role: 'user', text }, pendingMessage]);
    setDraft('');
    try {
      const { data } = await axios.post('/api/v1/warehouse/ask', askPayload({ focus, question: text, messages }));
      const result = data.result || data;
      const moves = Array.isArray(result.focus?.moves) ? result.focus.moves : [];
      if (moves.some((move) => move?.executed === true)) await onRefresh?.();
      const locationCodes = [...new Set([
        ...(Array.isArray(result.focus?.locationCodes) ? result.focus.locationCodes : []),
        ...moves.flatMap((move) => [move?.from, move?.to]),
      ].filter((code) => typeof code === 'string' && code.length > 0))];
      const rackLabel = result.focus?.rackLabel;
      if (locationCodes.length) {
        const moveFocus = moves.length > 0;
        onSceneCommand({
          camera: {
            locationCodes,
            zoom: moveFocus ? 1.8 : 2.3,
            label: moveFocus
              ? `${moves.every((move) => /\b(receiving|staging)\b/i.test(move.from)) ? 'Put away' : 'Consolidate'} ${moves[0].sku}`
              : result.focus.skus?.[0] || locationCodes[0],
          },
          highlight: moveFocus
            ? { locationCodes, skus: result.focus?.skus || [], moves }
            : { locationCodes, skus: result.focus?.skus || [] },
        });
      } else if (rackLabel) {
        onSceneCommand({
          camera: { rack: rackLabel, zoom: 1.75, label: `${rackLabel} rack` },
          highlight: { rack: rackLabel },
        });
      }
      setMessages((current) => current.map((message) => (
        message === pendingMessage
          ? { role: 'kip', text: result.reply, governanceRefs: result.governanceRefs, suggestedCommands: result.suggestedCommands, confidence: result.confidence, toolsUsed: result.toolsUsed, focus: result.focus, moves }
          : message
      )));
      if (locationCodes.length) {
        const rows = locationCodes.slice(0, 8).map((code) => model.byCode.get(code)).filter(Boolean);
        const extra = locationCodes.length - rows.length;
        setMessages((current) => current.map((message) => (
          message === pendingMessage
            ? { ...message, locationRows: rows, extraLocations: extra }
            : message
        )));
      }
    } catch (requestError) {
      const message = kipErrorMessage(requestError);
      setMessages((current) => current.map((item) => (item === pendingMessage ? { role: 'kip', text: message } : item)));
    }
    setDraft('');
  };

  return (
    <aside className="flex h-[48vh] min-h-0 w-full shrink-0 flex-col bg-white lg:h-full lg:w-[34vw] lg:min-w-[380px] lg:max-w-[520px]" aria-label="KIP conversation">
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-gray-200 px-4">
        <img src="/kip-logo.png" alt="KIP" className="h-8 w-auto object-contain" />
        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-500"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />Eadin connected</span>
      </header>
      <div ref={scrollRef} className="thin-scroll min-h-0 flex-1 overflow-y-auto px-4 py-4">
        <div className="space-y-5">{messages.map((message, index) => <ConversationMessage key={`${message.role}-${index}`} message={message} onCommand={submit} />)}</div>
      </div>
      <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-3">
        <form onSubmit={submit} className="rounded-3xl border border-gray-300 bg-white pl-4 pr-2 pt-2.5 pb-2 focus-within:border-gray-900 focus-within:ring-1 focus-within:ring-gray-900">
          <label className="block"><span className="sr-only">Message KIP</span><textarea ref={composerRef} aria-label="Message KIP" value={draft} onChange={(event) => setDraft(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); submit(event); } }} rows="1" placeholder="Ask KIP about this warehouse…" className="block max-h-[200px] min-h-[24px] w-full resize-none overflow-y-auto border-0 bg-transparent p-0 text-sm leading-5 text-gray-900 outline-none placeholder:text-gray-400" /></label>
          <div className="mt-0.5 flex justify-end">
            <button type="submit" disabled={!draft.trim()} className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white ${draft.trim() ? 'bg-black hover:bg-gray-800' : 'bg-gray-300'}`} title="Send message"><PaperAirplaneIcon className="h-4 w-4" /></button>
          </div>
        </form>
        <p className="mt-2 text-[10px] leading-4 text-gray-400">KIP governs · Eadin operates · ShipHero reflects physical truth</p>
      </div>
    </aside>
  );
};

export default ConversationPane;
